import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { GetAirlineResponse } from '@/api/generated/schemas';
import { FormActionBar, Tabs, WindowContent, toast } from '@/shared/ui';
import { cn } from '@/shared/lib/utils';
import { serverErrorDetail } from '@/shared/lib/problemDetails';
import { useAgendaParams } from '@/shared/navigation/react/useAgendaParams';
import { useAgendaState } from '@/shared/navigation/react/useAgendaState';
import { useNavigator } from '@/shared/navigation/react/useNavigator';

import { invalidateAirline } from '../internal/airlinesQueryKeys';
import { airlineDetailAgenda, type AirlineTabKey } from './airlineDetailAgenda';
import { AirlineExtraServicesTab } from './components/AirlineExtraServicesTab';
import { AirlineGeneralTab } from './components/AirlineGeneralTab';
import { useAirlineForm } from './useAirlineForm';
import { AIRLINE_EXTRA_SERVICES_FIELDS, type AirlineFormValues } from './airlineFormSchema';

export interface AirlineDetailViewProps {
  layerId: string;
  airlineId?: number;
  initialValues: AirlineFormValues;
  /**
   * Načtený detail — na rozdíl od `initialValues` (výchozí hodnoty formuláře, snímek při mountu)
   * je to **živá** serverová podoba záznamu. Sekce zavazadel z ní čte pravidla i persistovanou měnu,
   * protože se ukládají mimo formulář a musí se srovnávat s tím, co je opravdu v databázi.
   */
  airline?: GetAirlineResponse;
}

/**
 * Formulář detailu letecké společnosti, jeho záložky a sdílená lišta akcí v patičce okna.
 * Obě záložky jsou pole jednoho formuláře, takže patička platí pro obě.
 */
export function AirlineDetailView({
  layerId,
  airlineId,
  initialValues,
  airline,
}: AirlineDetailViewProps) {
  const { t } = useTranslation('inventory');
  const navigator = useNavigator();
  const queryClient = useQueryClient();

  const { state } = useAgendaParams(airlineDetailAgenda, layerId);
  const agendaState = useAgendaState(airlineDetailAgenda, layerId);
  const activeTab: AirlineTabKey = state.tab ?? 'general';
  const setActiveTab = useCallback(
    (tab: AirlineTabKey) => agendaState.patch({ tab }),
    [agendaState]
  );

  // Uložená měna dopravce — sekce zavazadel podle ní pozná neuloženou změnu i v čem zobrazit ceny.
  const carrierCurrency =
    airline?.currencyId == null
      ? null
      : { id: airline.currencyId, code: airline.currencyCode ?? '' };

  const close = useCallback(() => {
    void navigator.closeLayer(layerId);
  }, [navigator, layerId]);

  const { form, submit } = useAirlineForm({
    airlineId,
    initialValues,
    onSaved: (id, mode) => {
      // Detail (Orval klíč) i seznam (prefix klíče ListPage configu) — viz
      // `frontend/GUIDELINES.md` → „Detail stránky — čerstvost dat".
      invalidateAirline(queryClient, id);
      toast({ severity: 'success', description: t('airline.toast.saved') });
      if (mode === 'created') {
        // Po založení se okno překlopí do editace — `airlineId` žije v `open` parametrech agendy,
        // takže bez výměny vrstvy by další Save poslal druhý POST a unikátnost IATA/ICAO by ho
        // odmítla konfliktem na kód, který uživatel právě uložil. `replaceModal` zdědí zónu, takže
        // vrstva zůstane v detail panelu.
        void navigator.replaceModal(airlineDetailAgenda, { airlineId: id });
      }
    },
    onError: (error) => {
      // Konflikt IATA/ICAO i business pravidla nesou konkrétní větu v `detail` (RFC 9457) —
      // bez ní by uživatel u kolize kódů viděl jen „nepodařilo se uložit".
      toast({
        severity: 'error',
        title: t('airline.toast.errorTitle'),
        description: serverErrorDetail(error) ?? t('airline.toast.saveError'),
      });
    },
  });

  /**
   * Uloží a při neúspěchu odhalí záložku, která chybu drží. Povinné volby záložky Extra services
   * nemají v režimu zakládání default, takže bez tohohle by uživatel z General tabu viděl jen
   * neúspěšné uložení a chybu by měl schovanou na druhé záložce.
   */
  const submitAndRevealErrors = useCallback(async () => {
    const saved = await submit();
    if (
      !saved &&
      AIRLINE_EXTRA_SERVICES_FIELDS.some((name) => form.getFieldMeta(name)?.errors.length)
    ) {
      setActiveTab('extraServices');
    }
    return saved;
  }, [submit, form, setActiveTab]);

  return (
    <WindowContent
      as="form"
      width="reading"
      onSubmit={(event) => {
        event.preventDefault();
        void submitAndRevealErrors();
      }}
      banner={
        <Tabs<AirlineTabKey>
          value={activeTab}
          onChange={setActiveTab}
          items={[
            { value: 'general', label: t('airline.tabs.general') },
            { value: 'extraServices', label: t('airline.tabs.extraServices') },
          ]}
        />
      }
      footer={
        <form.Subscribe
          selector={(formState) => ({
            isDirty: formState.isDirty,
            canSubmit: formState.canSubmit,
            isSubmitting: formState.isSubmitting,
          })}
        >
          {({ isDirty, canSubmit, isSubmitting }) => (
            <FormActionBar
              onBack={close}
              onSave={submitAndRevealErrors}
              saving={isSubmitting}
              // Záměrně BEZ `disabled={!canSubmit}` (na rozdíl od jednozáložkových formulářů):
              // povinné volby žijí na druhé záložce, takže zašedlé Uložit by nešlo vysvětlit.
              // Kliknutí označí pole jako dotčená, vykreslí chyby a odhalí záložku s nimi.
              statusNote={
                <>
                  <span
                    className={cn(
                      'mr-phx-2 inline-block h-1.5 w-1.5 rounded-full',
                      canSubmit
                        ? isDirty
                          ? 'bg-phx-warning'
                          : 'bg-phx-text-faint'
                        : 'bg-phx-danger'
                    )}
                  />
                  {!canSubmit
                    ? t('airline.status.incomplete')
                    : isDirty
                      ? t('airline.status.unsaved')
                      : t('airline.status.clean')}
                </>
              }
            />
          )}
        </form.Subscribe>
      }
    >
      {/* Obě záložky zůstávají připojené — skrytá pole formuláře musí dál validovat a odeslat se. */}
      <div className={cn(activeTab !== 'general' && 'hidden')}>
        <AirlineGeneralTab form={form} />
      </div>
      <div className={cn(activeTab !== 'extraServices' && 'hidden')}>
        <AirlineExtraServicesTab
          form={form}
          airlineId={airlineId}
          baggageRules={airline?.baggageRules ?? []}
          carrierCurrency={carrierCurrency}
        />
      </div>
    </WindowContent>
  );
}
